import * as React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
  Stack,
  Divider,
  IconButton,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RichTextEditor } from './components/RichTextEditor';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { sanitizeTelegramHtml } from './utils';

const API_URL = import.meta.env.VITE_API_URL;

type OrderField =
  | 'mod_order_fio'
  | 'mod_order_number'
  | 'mod_order_shop'
  | 'mod_order_date'
  | 'mod_order_success_title'
  | 'mod_order_success_order_number'
  | 'mod_order_success_fio'
  | 'mod_order_success_phone'
  | 'mod_order_success_city'
  | 'mod_order_success_shop'
  | 'mod_order_success_date'
  | 'mod_order_success_quantity'
  | 'mod_order_success_price'
  | 'mod_order_success_price_total'
  | 'mod_order_success_price_due'
  | 'mod_order_success_footer'
  | 'mod_order_final_text'
  | 'list_product_characteristic'
  | 'list_product_compose'
  | 'list_product_description'
  | 'list_product_count'
  | 'list_product_name'
  | 'list_product_header'
  | 'list_product_price'
  | 'list_product_price_total'
  | 'list_product_subtotal'
  | 'list_product_footer'
  | 'list_product_extra_1'
  | 'list_product_extra_2'
  | 'list_product_extra_3';

type OrderType =
  | 'order_process'
  | 'order_final'
  | 'order_final_end'
  | 'list_product';

type OrderCardItem = {
  orderCardId: number;
  value: string;
  field: OrderField;
  isActive: boolean | null;
  index: number;
  type: OrderType;
  spaces: number | null;
  createdAt: string;
  updatedAt: string;
};

const FIELD_LABELS: Record<OrderField, string> = {
  mod_order_fio: 'Запит на ПІБ користувача',
  mod_order_number: 'Запит на номер користувача',
  mod_order_shop: 'Запит на вибір магазину',
  mod_order_date: 'Запит на вибір дати',
  mod_order_success_title: 'Напис про прийняте замовлення',
  mod_order_success_order_number: 'Номер замовлення',
  mod_order_success_fio: 'ПІБ користувача',
  mod_order_success_phone: 'Телефон користувача',
  mod_order_success_city: 'Місто користувача',
  mod_order_success_shop: 'Обраний магазин',
  mod_order_success_date: 'Дата замовлення',
  mod_order_success_quantity: 'Кількість продукції',
  mod_order_success_price: 'Ціна за одиницю',
  mod_order_success_price_total: 'Загальна сума',
  mod_order_success_price_due: 'Сума до сплати',
  mod_order_success_footer: 'Напис в кінці',
  mod_order_final_text:
    'Замовлення прийнято і обробляється: повідомлення для користувача',
  list_product_characteristic: 'Характеристика продукту',
  list_product_compose: 'Склад продукту',
  list_product_description: 'Опис продукту',
  list_product_count: 'Кількість продукту',
  list_product_name: 'Назва продукту',
  list_product_header: 'Заголовок списку продукції',
  list_product_price: 'Ціна за одиницю продукту',
  list_product_price_total: 'Загальна сума',
  list_product_subtotal: 'Підсумкова сума',
  list_product_footer: 'Напис під списком продукції',
  list_product_extra_1: 'Додаткове поле №1',
  list_product_extra_2: 'Додаткове поле №2',
  list_product_extra_3: 'Додаткове поле №3',
};

const FIELDS_WITH_NAME_MACRO: OrderField[] = ['mod_order_number'];

const LIST_PRODUCT_FIXED_FIELDS: OrderField[] = [
  'list_product_header',
  'list_product_footer',
  'list_product_subtotal',
];

function SpacesToggle({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        Відступ
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        onChange={(_, v) => v !== null && onChange(v)}
      >
        {[0, 1, 2, 3, 4, 5].map((n) => (
          <ToggleButton key={n} value={n} sx={{ minWidth: 32, px: 0 }}>
            {n}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}

function ProcessRow({
  item,
  onChange,
}: {
  item: OrderCardItem;
  onChange: (id: number, patch: Partial<OrderCardItem>) => void;
}) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {FIELD_LABELS[item.field] ?? item.field}
        </Typography>
        <RichTextEditor
          value={item.value}
          onChange={(html) => onChange(item.orderCardId, { value: html })}
          minRows={2}
        />
        {FIELDS_WITH_NAME_MACRO.includes(item.field) && (
          <Typography variant="caption" color="text.secondary">
            Використовуйте макрос {'${name}'} для відображення імені користувача
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function SortableFinalRow({
  item,
  onChange,
}: {
  item: OrderCardItem;
  onChange: (id: number, patch: Partial<OrderCardItem>) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: item.orderCardId,
    });

  return (
    <Paper
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{ p: 2 }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab' }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            flex={1}
          >
            {FIELD_LABELS[item.field] ?? item.field}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!item.isActive}
                onChange={(e) =>
                  onChange(item.orderCardId, { isActive: e.target.checked })
                }
              />
            }
            label="Відобразити"
            sx={{ mr: 0 }}
          />
        </Stack>
        <RichTextEditor
          value={item.value}
          onChange={(html) => onChange(item.orderCardId, { value: html })}
          minRows={2}
        />
        <SpacesToggle
          value={item.spaces ?? 0}
          onChange={(v) => onChange(item.orderCardId, { spaces: v })}
        />
      </Stack>
    </Paper>
  );
}

function SortableListProductRow({
  item,
  onChange,
}: {
  item: OrderCardItem;
  onChange: (id: number, patch: Partial<OrderCardItem>) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: item.orderCardId,
    });

  return (
    <Paper
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{ p: 2 }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab' }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            flex={1}
          >
            {FIELD_LABELS[item.field] ?? item.field}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!item.isActive}
                onChange={(e) =>
                  onChange(item.orderCardId, { isActive: e.target.checked })
                }
              />
            }
            label="Відобразити"
            sx={{ mr: 0 }}
          />
        </Stack>
        <RichTextEditor
          value={item.value}
          onChange={(html) => onChange(item.orderCardId, { value: html })}
          minRows={2}
        />
        <SpacesToggle
          value={item.spaces ?? 0}
          onChange={(v) => onChange(item.orderCardId, { spaces: v })}
        />
      </Stack>
    </Paper>
  );
}

function ListProductFixedRow({
  item,
  onChange,
}: {
  item: OrderCardItem;
  onChange: (id: number, patch: Partial<OrderCardItem>) => void;
}) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            flex={1}
          >
            {FIELD_LABELS[item.field] ?? item.field}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!item.isActive}
                onChange={(e) =>
                  onChange(item.orderCardId, { isActive: e.target.checked })
                }
              />
            }
            label="Відобразити"
            sx={{ mr: 0 }}
          />
        </Stack>
        <RichTextEditor
          value={item.value}
          onChange={(html) => onChange(item.orderCardId, { value: html })}
          minRows={2}
        />
        <SpacesToggle
          value={item.spaces ?? 0}
          onChange={(v) => onChange(item.orderCardId, { spaces: v })}
        />
      </Stack>
    </Paper>
  );
}

function FooterBlock({
  item,
  onChange,
}: {
  item: OrderCardItem;
  onChange: (id: number, patch: Partial<OrderCardItem>) => void;
}) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {FIELD_LABELS['mod_order_success_footer']}
        </Typography>
        <RichTextEditor
          value={item.value}
          onChange={(html) => onChange(item.orderCardId, { value: html })}
          minRows={4}
        />
      </Stack>
    </Paper>
  );
}

function FinalEndBlock({
  item,
  onChange,
}: {
  item: OrderCardItem;
  onChange: (id: number, patch: Partial<OrderCardItem>) => void;
}) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {FIELD_LABELS[item.field]}
        </Typography>
        <RichTextEditor
          value={item.value}
          onChange={(html) => onChange(item.orderCardId, { value: html })}
          minRows={3}
        />
      </Stack>
    </Paper>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Stack spacing={0.5} mb={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Stack>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Stack>
      <Stack spacing={2}>{children}</Stack>
    </Box>
  );
}

export default function ModuleOrders() {
  const [isMacrosOn, setIsMacrosOn] = React.useState<boolean | null>(null);
  const [items, setItems] = React.useState<OrderCardItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetch(`${API_URL}/settings`)
      .then((r) => r.json())
      .then((res) => setIsMacrosOn(res.settings.isMacrosOn))
      .catch(() => setIsMacrosOn(false));

    fetch(`${API_URL}/order-card`)
      .then((r) => r.json())
      .then((res) => {
        setItems(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError('Не вдалося завантажити дані');
        setLoading(false);
      });
  }, []);

  const updateItem = async (id: number, patch: Partial<OrderCardItem>) => {
    const newPatch: Partial<OrderCardItem> = { ...patch };

    if (patch.value !== undefined) {
      newPatch.value = sanitizeTelegramHtml(patch.value);
    }

    setItems((prev) =>
      prev.map((i) => (i.orderCardId === id ? { ...i, ...newPatch } : i)),
    );

    await fetch(`${API_URL}/order-card/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newPatch),
    });
  };

  const onFinalDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.orderCardId === active.id);
    const newIndex = items.findIndex((i) => i.orderCardId === over.id);
    const newItems = arrayMove(items, oldIndex, newIndex);

    setItems(newItems);

    await fetch(`${API_URL}/order-card-reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order: newItems
          .filter(
            (i) =>
              i.type === 'order_final' &&
              i.field !== 'mod_order_success_footer',
          )
          .map((i, index) => ({ id: i.orderCardId, index })),
      }),
    });
  };

  const onListProductDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.orderCardId === active.id);
    const newIndex = items.findIndex((i) => i.orderCardId === over.id);
    const newItems = arrayMove(items, oldIndex, newIndex);

    setItems(newItems);

    await fetch(`${API_URL}/order-card-reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order: newItems
          .filter(
            (i) =>
              i.type === 'list_product' &&
              !LIST_PRODUCT_FIXED_FIELDS.includes(i.field),
          )
          .map((i, index) => ({ id: i.orderCardId, index })),
      }),
    });
  };

  const processItems = items.filter((i) => i.type === 'order_process');

  const finalItems = items.filter(
    (i) => i.type === 'order_final' && i.field !== 'mod_order_success_footer',
  );

  const finalEndItems = items.filter((i) => i.type === 'order_final_end');

  const footerItem = items.find((i) => i.field === 'mod_order_success_footer');

  const listProductSortable = items.filter(
    (i) =>
      i.type === 'list_product' && !LIST_PRODUCT_FIXED_FIELDS.includes(i.field),
  );

  const listProductFixed = items.filter(
    (i) =>
      i.type === 'list_product' && LIST_PRODUCT_FIXED_FIELDS.includes(i.field),
  );

  if (loading)
    return (
      <Box p={3}>
        <Typography color="text.secondary">Завантаження...</Typography>
      </Box>
    );
  if (error)
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );

  if (isMacrosOn) {
    return (
      <Alert severity={'error'} icon={<CancelIcon />} sx={{ mb: 3, mt: 2 }}>
        <b>Увага:</b> Через використання макросів розділ цих налаштувань
        недоступний. Шоб використовувати ці налаштування вимкніть макроси у{' '}
        <a href="/settings">налаштуваннях</a>.
      </Alert>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h6" mb={3}>
        Налаштування картки замовлення
      </Typography>

      <Box display="flex" alignItems="center" mb={2}>
        <Typography>
          Картка замовлення - це повідомлення, яке користувач бачить до та після
          оформлення замовлення. Тут можна налаштувати, які поля відображати, їх
          порядок та відступи між ними.
        </Typography>

        <Tooltip
          title={
            <img
              src={`/instruction/27.jpg`}
              alt="inline preview"
              style={{ width: 250, borderRadius: 8 }}
            />
          }
          placement="bottom"
        >
          <IconButton size="small" sx={{ ml: 1 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Stack spacing={4}>
        <Section
          title="Оформлення замовлення"
          description="У цьому розділі можна змінити повідомлення що надходять користувачу при оформленні замовлення"
        >
          {processItems.map((item) => (
            <ProcessRow
              key={item.orderCardId}
              item={item}
              onChange={updateItem}
            />
          ))}
        </Section>

        <Section
          title="Завершене замовлення"
          description="У цьому розділі можна змінити повідомлення що надходять користувачу після оформлення замовлення та його обробки. Тут можна налаштувати які поля відображати, їх порядок та відступи між ними. Також можна налаштувати окреме повідомлення, яке буде в кінці замовлення, після всіх товарів та суми до сплати."
        >
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={onFinalDragEnd}
          >
            <SortableContext
              items={finalItems.map((i) => i.orderCardId)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={2}>
                {finalItems.map((item) => (
                  <SortableFinalRow
                    key={item.orderCardId}
                    item={item}
                    onChange={updateItem}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </Section>

        <Section title="Фінальний текст">
          {finalEndItems.map((item) => (
            <FinalEndBlock
              key={item.orderCardId}
              item={item}
              onChange={updateItem}
            />
          ))}
        </Section>

        {footerItem && (
          <Section title="Напис в кінці замовлення">
            <FooterBlock item={footerItem} onChange={updateItem} />
          </Section>
        )}

        <Section
          title="Відображення інформації про продукт"
          description="У цьому розділі можна налаштувати, які поля про продукт відображати в замовленні, їх порядок та відступи між ними. Це інформація що буде відображатися для кожного товару в замовленні."
        >
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={onListProductDragEnd}
          >
            <SortableContext
              items={listProductSortable.map((i) => i.orderCardId)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={2}>
                {listProductSortable.map((item) => (
                  <SortableListProductRow
                    key={item.orderCardId}
                    item={item}
                    onChange={updateItem}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>

          {listProductFixed.length > 0 && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Фіксовані елементи
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Stack>
              {listProductFixed.map((item) => (
                <ListProductFixedRow
                  key={item.orderCardId}
                  item={item}
                  onChange={updateItem}
                />
              ))}
            </Stack>
          )}
        </Section>
      </Stack>
    </Box>
  );
}
